import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { DialogLiveRegion } from "../../shared/components/DialogLiveRegion";
import { findTarget, onPhone, stepBody, stepTarget, text } from "./registry";
import type { TourDef, TourStep } from "./tours";
import "./guides.css";

export type TourEnd = "finished" | "skipped" | "closed" | "unavailable";

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

const MARGIN = 12;
const CARD_WIDTH = 360;
const ROOM = 200;
const PAD = 6;
/** How long a step follows its element after it shows (see the measuring effect). */
const SETTLE_MS = 1200;

/** Steps that can show now: centred steps, and steps whose element (the phone one on a phone) is on the page. */
function showableSteps(steps: readonly TourStep[]): number[] {
  const phone = onPhone();
  return steps.flatMap((step, index) => {
    const target = stepTarget(step, phone);
    return !target || findTarget(target) ? [index] : [];
  });
}

function measure(step: TourStep | undefined): Box | null {
  const target = step ? stepTarget(step) : undefined;
  const element = target ? findTarget(target) : null;
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  // Hidden by layout (e.g. the side nav on a phone): show the step centred, without a highlight.
  if (rect.width === 0 && rect.height === 0) return null;
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
}

/** Below the target when there is room, else above, else beside it (a tall target such as the side nav), else pinned to
 * the top; always inside the viewport. */
function cardPlacement(box: Box): CSSProperties {
  const width = Math.min(CARD_WIDTH, window.innerWidth - 2 * MARGIN);
  const left = Math.min(Math.max(box.left, MARGIN), Math.max(MARGIN, window.innerWidth - width - MARGIN));
  const below = box.top + box.height + PAD + MARGIN;
  if (window.innerHeight - below >= ROOM) return { top: below, left, width };
  if (box.top - PAD - MARGIN >= ROOM) return { bottom: window.innerHeight - box.top + PAD + MARGIN, left, width };
  const top = Math.min(Math.max(box.top, MARGIN), Math.max(MARGIN, window.innerHeight - ROOM - MARGIN));
  const right = box.left + box.width + PAD + MARGIN;
  if (window.innerWidth - right - MARGIN >= width) return { top, left: right, width };
  if (box.left - PAD - MARGIN >= width + MARGIN) return { top, left: box.left - PAD - MARGIN - width, width };
  return { top: MARGIN, left, width };
}

interface TourOverlayProps {
  tour: TourDef;
  onEnd: (reason: TourEnd) => void;
  /** Called once, when the first step is on screen (never for a tour that ends "unavailable"). */
  onShown?: () => void;
  /** How long to wait for the tour's first element to appear (a page still loading) before giving up. */
  waitMs?: number;
}

/**
 * The spotlight: a modal dialog in the top layer that dims the page, cuts a highlight around the step's element and
 * places a card beside it with ถัดไป / ย้อนกลับ / ข้าม and a step counter. Esc closes; the arrow keys step; focus moves
 * to the main button at each step and returns to where it was when the tour ends. A step whose element is missing is
 * skipped; a tour with nothing to show ends as "unavailable" and nothing is recorded.
 */
export function TourOverlay({ tour, onEnd, onShown, waitMs = 3000 }: TourOverlayProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const ended = useRef(false);
  const titleId = useId();
  const bodyId = useId();
  // Which steps can show, re-read at every move (a list may finish loading mid-tour), and the step on screen.
  const [present, setPresent] = useState<number[] | null>(null);
  const [current, setCurrent] = useState<number | null>(null);
  const [box, setBox] = useState<Box | null>(null);

  const end = useCallback(
    (reason: TourEnd) => {
      if (ended.current) return;
      ended.current = true;
      onEnd(reason);
    },
    [onEnd],
  );

  // Start as soon as something of the tour is on the page; a page opened for a replay may still be loading.
  useEffect(() => {
    const started = Date.now();
    const timer = window.setInterval(check, 100);
    const first = window.setTimeout(check, 0);
    function check() {
      const found = showableSteps(tour.steps);
      if (found.length > 0 || Date.now() - started >= waitMs) {
        window.clearInterval(timer);
        window.clearTimeout(first);
        setPresent(found);
        setCurrent(found[0] ?? null);
      }
    }
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(first);
    };
  }, [tour, waitMs]);

  useEffect(() => {
    if (present && present.length === 0) end("unavailable");
  }, [present, end]);

  // Shown means the person has had the tour in front of them, however it then ends (a closed tab included).
  const shown = useRef(false);
  useEffect(() => {
    if (shown.current || !present || present.length === 0) return;
    shown.current = true;
    onShown?.();
  }, [present, onShown]);

  // Open as a modal (top layer, inert page, Esc) and give focus back when the tour ends.
  useEffect(() => {
    const dialog = dialogRef.current;
    const opener = document.activeElement;
    if (dialog && !dialog.open) dialog.showModal();
    return () => {
      if (dialog?.open) dialog.close();
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, []);

  const step = current === null ? undefined : tour.steps[current];

  // Bring the element into view, then measure it; follow scrolling and resizing.
  useEffect(() => {
    if (!step) return undefined;
    const target = stepTarget(step);
    const element = target ? findTarget(target) : null;
    element?.scrollIntoView({ block: "center", inline: "nearest" });
    // Batch 18 §11.4: the page can still move under the tour after it starts (on a phone the account sheet that started the
    // replay is closing and the header settles), with no resize or scroll to tell us. Follow the element for a moment, frame by
    // frame, keeping the box only when it really moved.
    const update = () => setBox((before) => {
      const now = measure(step);
      return before && now && before.top === now.top && before.left === now.left && before.width === now.width && before.height === now.height ? before : now;
    });
    const settleUntil = performance.now() + SETTLE_MS;
    let frame = 0;
    const follow = () => {
      update();
      frame = performance.now() < settleUntil ? window.requestAnimationFrame(follow) : 0;
    };
    frame = window.requestAnimationFrame(follow);
    const remeasure = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };
    window.addEventListener("resize", remeasure);
    window.addEventListener("scroll", remeasure, true);
    primaryRef.current?.focus();
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("scroll", remeasure, true);
    };
  }, [step]);

  const total = present?.length ?? 0;
  const position = current === null || !present ? 0 : Math.max(0, present.indexOf(current));
  const last = !present?.some((index) => current !== null && index > current);
  const next = () => {
    if (current === null) return;
    const found = showableSteps(tour.steps);
    const after = found.find((index) => index > current);
    if (after === undefined) {
      end("finished");
      return;
    }
    setPresent(found);
    setCurrent(after);
  };
  const back = () => {
    if (current === null) return;
    const found = showableSteps(tour.steps);
    const before = found.filter((index) => index < current).pop();
    setPresent(found);
    if (before !== undefined) setCurrent(before);
  };
  const highlight = step && stepTarget(step) ? box : null;

  return createPortal(
    <dialog
      ref={dialogRef}
      className="gf-tour"
      // Thai by product decision (2026-09-30, re-affirmed 2026-10-05): the English-only chrome checks skip this surface.
      data-chrome-exempt="guided-tour"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      onCancel={(event) => {
        event.preventDefault();
        end("closed");
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          end("closed");
        } else if (event.key === "ArrowRight" && step) {
          event.preventDefault();
          next();
        } else if (event.key === "ArrowLeft" && step) {
          event.preventDefault();
          back();
        }
      }}
    >
      {highlight ? (
        <div
          className="gf-tour-hole"
          aria-hidden="true"
          style={{ top: highlight.top - PAD, left: highlight.left - PAD, width: highlight.width + 2 * PAD, height: highlight.height + 2 * PAD }}
        />
      ) : (
        <div className="gf-tour-dim" aria-hidden="true" />
      )}
      {step && (
        <div className={highlight ? "gf-tour-card" : "gf-tour-card centred"} style={highlight ? cardPlacement(highlight) : undefined} lang="th">
          <p className="gf-tour-count">
            {text(tour.title)} · ขั้นที่ {position + 1} จาก {total}
          </p>
          <div aria-live="polite">
            <h2 id={titleId}>{text(step.title)}</h2>
            <p id={bodyId}>{text(stepBody(step))}</p>
          </div>
          <div className="gf-tour-actions">
            <button type="button" className="linkish" onClick={() => end("skipped")}>
              ข้าม
            </button>
            <span className="gf-tour-nav">
              <button type="button" className="btn sec" onClick={back} disabled={position === 0}>
                ย้อนกลับ
              </button>
              <button ref={primaryRef} type="button" className="btn" onClick={next} data-autofocus>
                {last ? "เสร็จสิ้น" : "ถัดไป"}
              </button>
            </span>
          </div>
        </div>
      )}
      {/* Batch 26: the app's announcements speak here while the tour (a modal dialog) is open. */}
      <DialogLiveRegion />
    </dialog>,
    document.body,
  );
}
